import type { input } from 'zod';
import { useFeature } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Divider, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { DialogType, SelectOption } from '../../../@types';
import { Marketplace, MarketplaceDomain } from '../../../@types/api';
import { QUERY_KEYS } from '../../../constants';
import { useModal } from '../../../contexts/ModalContext';
import useMutateData from '../../../hooks/useMutateData';
import { MarketplaceFormData, marketplaceFormSchema } from '../../../schema';
import FormModalLayout from '../../layouts/FormModalLayout';
import CountrySelect from '../../molecules/CountrySelect';
import FormSelect from '../../molecules/FormSelect';
import FormFieldStack from '../../atoms/FormFieldStack';
import Switch from '../../atoms/Switch';
import { STATUS } from '../../../constants/enums';
import type { StyleObj } from '../../../@types';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import API_ROUTES from '../../../constants/apiRoutes';

import { mergeSx } from '../../../utils/styles';

const styles: StyleObj = {
  domainCard: {
    p: 2,
    borderRadius: 2,
    backgroundColor: 'background.light',
    border: '1px solid',
    borderColor: 'divider',
  },
  domainCardHeader: {
    mb: 2,
  },
  domainCardTitle: {
    fontWeight: 600,
  },
};

const DEFAULT_DOMAIN_DATA: MarketplaceDomain = {
  domain: '',
  branding: {
    skin: '',
    theme: '',
  },
};

const DEFAULT_FORM_DATA: MarketplaceFormData = {
  name: undefined,
  status: STATUS.ACTIVE,
  country: undefined,
  domains: [DEFAULT_DOMAIN_DATA],
  contactInformation: {
    contactName: undefined,
    contactEmail: '',
    contactPhoneNumber: undefined,
  },
  sanityConfig: {
    projectId: '',
    dataset: '',
  },
};

type Props = DialogType;

const MarketplaceForm = ({ closeModal }: Props) => {
  const { item } = useModal<Marketplace>();
  const { createData, updateData } = useMutateData(API_ROUTES.MARKETPLACE, [QUERY_KEYS.marketplace], 'crm');

  const { value: themeOptions } = useFeature<SelectOption[]>('theme-options');
  const { value: skinOptions } = useFeature<SelectOption[]>('skin-options');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<input<typeof marketplaceFormSchema>, unknown, MarketplaceFormData>({
    defaultValues: DEFAULT_FORM_DATA,
    resolver: zodResolver(marketplaceFormSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'domains',
  });

  useEffect(() => {
    if (item) {
      reset(item);
    }
  }, [item, reset]);

  const handleClose = () => {
    reset(DEFAULT_FORM_DATA);
    closeModal?.();
  };

  const onFormSubmit = (data: MarketplaceFormData) => {
    if (item) {
      updateData(item.id, data, handleClose);
    } else {
      createData(data, handleClose);
    }
  };

  const isEdit = !!item;

  return (
    <FormModalLayout
      onSave={handleSubmit(onFormSubmit)}
      label={isEdit ? 'Configure marketplace' : 'Add new marketplace'}
      onClose={handleClose}
      isEdit={isEdit}
    >
      <TextField
        error={!!errors.name}
        helperText={errors.name?.message}
        label='Name'
        {...register('name')}
        required
        autoFocus
      />

      <CountrySelect control={control} name='country' allowDeselect={false} error={errors.country} required />

      <Stack spacing={2}>
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography variant='subtitle1'>Domains</Typography>
          <IconButton color='primary' onClick={() => append(DEFAULT_DOMAIN_DATA)}>
            <AddCircle />
          </IconButton>
        </Stack>

        {fields.map((field, index) => (
          <Paper key={field.id} variant='outlined' sx={styles.domainCard}>
            <Stack
              direction='row'
              sx={mergeSx(
                {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
                styles.domainCardHeader
              )}
            >
              <Typography variant='subtitle2' sx={styles.domainCardTitle}>
                {`Domain ${index + 1}`}
              </Typography>

              <IconButton color='error' disabled={fields.length === 1} onClick={() => remove(index)}>
                <RemoveCircle />
              </IconButton>
            </Stack>

            <Stack spacing={2}>
              <TextField
                error={!!errors.domains?.[index]?.domain}
                helperText={errors.domains?.[index]?.domain?.message}
                label='API Domain/Subdomain'
                {...register(`domains.${index}.domain`)}
                required
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormSelect
                  name={`domains.${index}.branding.skin`}
                  control={control}
                  label='Skin'
                  required
                  options={skinOptions ?? []}
                  allowDeselect={false}
                  error={errors.domains?.[index]?.branding?.skin}
                />

                <FormSelect
                  name={`domains.${index}.branding.theme`}
                  control={control}
                  label='Theme'
                  options={themeOptions ?? []}
                  allowDeselect={false}
                  error={errors.domains?.[index]?.branding?.theme}
                  required
                />
              </Stack>
            </Stack>
          </Paper>
        ))}

        {typeof errors.domains?.message === 'string' && (
          <Typography variant='caption' color='error'>
            {errors.domains.message}
          </Typography>
        )}
      </Stack>

      <Typography variant='subtitle1'>Contact information</Typography>
      <Divider />
      <TextField
        label='Name'
        {...register('contactInformation.contactName')}
        required
        error={!!errors.contactInformation?.contactName}
        helperText={errors.contactInformation?.contactName?.message}
      />
      <TextField
        label='Email'
        {...register('contactInformation.contactEmail')}
        error={!!errors.contactInformation?.contactEmail}
        helperText={errors.contactInformation?.contactEmail?.message}
        required
      />
      <TextField
        label='Phone'
        {...register('contactInformation.contactPhoneNumber')}
        error={!!errors.contactInformation?.contactPhoneNumber}
        helperText={errors.contactInformation?.contactPhoneNumber?.message}
        required
      />
      <Divider />

      <Typography variant='subtitle1'>Sanity config</Typography>
      <Divider />
      <TextField
        label='Project ID'
        {...register('sanityConfig.projectId')}
        error={!!errors.sanityConfig?.projectId}
        helperText={errors.sanityConfig?.projectId?.message}
        required
      />
      <TextField
        label='Dataset'
        {...register('sanityConfig.dataset')}
        error={!!errors.sanityConfig?.dataset}
        helperText={errors.sanityConfig?.dataset?.message}
        required
      />
      <Divider />

      <FormFieldStack label='Status'>
        <Controller
          name='status'
          control={control}
          render={({ field }) => (
            <Switch
              value={field.value === STATUS.ACTIVE}
              onChange={(e) => setValue('status', e.target.checked ? STATUS.ACTIVE : STATUS.INACTIVE)}
              ref={null}
            />
          )}
        />
      </FormFieldStack>
    </FormModalLayout>
  );
};

export default MarketplaceForm;
